/*
 * Cart drawer (Dawn 16, FROSTBEULE Hotel – owner: B-Footer+Cart)
 * FROSTBEULE changes:
 * - Focus trap no longer depends on a single `transitionend`. Dawn armed the trap only on the
 *   first transitionend; when none fired (motion "none", or the transition was skipped) the
 *   drawer opened without a trap and focus stayed behind it. Now the trap is armed by whichever
 *   comes first: the transitionend of the panel/overlay or a fallback timer (0 ms in motion
 *   "none", otherwise slightly longer than the 420 ms slide). The slide/fade itself is unchanged.
 * - Escape is handled on `document` while the drawer is open, so it also works when focus is
 *   outside the drawer. An open <details> (order note) inside the drawer closes first.
 * - `fb-cart-drawer--primed` is set on the first open; until then the empty-state mascot is not
 *   laid out (component-cart-drawer.css), so its image is not fetched on every page view.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    clearTimeout(this.trapTimer);
    clearTimeout(this.activateTimer);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // Intent (hover/focus/touch on the cart icon) already primes the drawer, so the empty-state
    // mascot is fetched before the click and is there when the panel slides in.
    const prime = () => this.classList.add('fb-cart-drawer--primed');
    ['pointerenter', 'focus', 'touchstart'].forEach((type) =>
      cartLink.addEventListener(type, prime, { once: true, passive: true }),
    );
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  motionNone() {
    return document.documentElement.getAttribute('data-motion') === 'none';
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Every open gets its own token, so a late timer/transitionend of an earlier open never traps.
    const openToken = (this.openToken = (this.openToken || 0) + 1);
    let trapped = false;

    const onTransitionEnd = (event) => {
      // only the panel or the overlay finishing counts (not a button colour inside the drawer)
      if (event.target !== this && !event.target.matches('.drawer__inner, .cart-drawer__overlay')) return;
      trap();
    };

    // Focus can only land once the panel is rendered visible. With motion "none" the visibility
    // switch is a 0.01 ms transition that still starts at "hidden", so focus() right after .active
    // can silently fail → re-check on the next frames until focus is inside the drawer.
    const ensureFocus = (focusElement, attempt) => {
      if (openToken !== this.openToken || !this.classList.contains('active')) return;
      if (this.contains(document.activeElement)) return;
      if (attempt > 0) focusElement.focus();
      if (!this.contains(document.activeElement) && attempt < 12) {
        requestAnimationFrame(() => ensureFocus(focusElement, attempt + 1));
      }
    };

    const trap = () => {
      if (trapped || openToken !== this.openToken || !this.classList.contains('active')) return;
      trapped = true;
      this.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(this.trapTimer);

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (!containerToTrapFocusOn || !focusElement) return;
      trapFocus(containerToTrapFocusOn, focusElement);
      ensureFocus(focusElement, 0);
    };

    this.addEventListener('transitionend', onTransitionEnd);
    this.removePendingTransitionListener = () => this.removeEventListener('transitionend', onTransitionEnd);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.isOpening = true;
    clearTimeout(this.activateTimer);
    this.activateTimer = setTimeout(() => {
      if (openToken !== this.openToken) return;
      this.isOpening = false;
      this.classList.add('animate', 'active', 'fb-cart-drawer--primed');
      // Fallback: arm the trap even if no transition fires (motion "none", skipped transition).
      clearTimeout(this.trapTimer);
      this.trapTimer = setTimeout(trap, this.motionNone() ? 0 : 450);
    });

    document.addEventListener('keydown', this.onDocumentKeydown);
    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    const wasOpen = this.classList.contains('active');
    // Not open and not opening: leave the page (and any other dialog's focus trap) untouched.
    if (!wasOpen && !this.isOpening) return;

    this.isOpening = false;
    this.openToken = (this.openToken || 0) + 1;
    clearTimeout(this.activateTimer);
    clearTimeout(this.trapTimer);
    if (this.removePendingTransitionListener) this.removePendingTransitionListener();
    document.removeEventListener('keydown', this.onDocumentKeydown);

    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    if (wasOpen) removeTrapFocus(this.activeElement);
  }

  onDocumentKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (event.defaultPrevented || !this.classList.contains('active')) return;

    // An open order note (<details>) closes first (Dawn's onKeyUpEscape on keyup), the drawer next time.
    const openDetails = event.target instanceof Element ? event.target.closest('details[open]') : null;
    if (openDetails && this.contains(openDetails)) return;

    // A quantity-rules popover inside the drawer closes first, too.
    const openInfo = this.querySelector('.quantity-popover__info:not([hidden])');
    if (openInfo && openInfo.contains(event.target)) return;

    event.preventDefault();
    this.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
